import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Calendar,
  QrCode, Loader2, CheckCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { venueApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';
import { toast } from 'sonner';
import VenueHeader from '../../components/venue/VenueHeader';

const VenueAnalytics = () => {
  const { user, loading: authLoading } = useAuth();
  const [location, setLocation] = useState(null);
  const [stats, setStats] = useState({
    totalScans: 0,
    scansToday: 0,
    scansThisWeek: 0,
    scansThisMonth: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for AuthContext to resolve before touching user.venue_partner_id.
    // The old one-shot [] effect dereferenced it on first render and crashed
    // the page on any direct load or refresh.
    if (authLoading) return;
    // Access is enforced by <RequireVenue> in App.js; a partner whose profile
    // carries no hub yet simply has nothing to fetch.
    if (!user?.venue_partner_id) {
      setLoading(false);
      return;
    }
    fetchAnalytics();
  }, [user, authLoading]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const venueResponse = await venueApi.get(
        `/venue/partner/${user.venue_partner_id}`
      );

      if (venueResponse.data.hub_location_id) {
        const locationResponse = await venueApi.get(
          `/locations/${venueResponse.data.hub_location_id}`
        );
        setLocation(locationResponse.data);

        setStats({
          totalScans: locationResponse.data.scan_count || 0,
          scansToday: 0,
          scansThisWeek: 0,
          scansThisMonth: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // V5 Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <img src="/tappd-logo.png" alt="Tappd" className="h-12 mx-auto mb-6" />
          <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: '#14b8a6' }} />
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Venue Header with Mobile Nav */}
      <VenueHeader user={user} venuePartner={null} locationData={location} />

      {/* Content */}
      <div className="max-w-[1280px] mx-auto px-6 py-8 pt-24">
        {/* V5 Page Header */}
        <div className="mb-8">
          <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // venue_analytics
          </p>
          <h1 
            className="text-[32px] mb-1"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            Analytics & Insights
          </h1>
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Track QR code scans and customer engagement
          </p>
        </div>

        {/* V5 Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {/* Total Scans - Teal */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', borderRadius: '12px' }}
            >
              <QrCode size={24} style={{ color: '#14b8a6' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Total Scans</p>
            <p className="text-[32px] font-bold" style={{ color: '#14b8a6' }}>
              {stats.totalScans.toLocaleString()}
            </p>
            <p className="text-[12px]" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>All time</p>
          </motion.div>

          {/* Today - Green */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #22c55e',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px' }}
            >
              <Calendar size={24} style={{ color: '#22c55e' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Today</p>
            <p className="text-[32px] font-bold" style={{ color: '#22c55e' }}>
              {stats.scansToday.toLocaleString()}
            </p>
            <p className="text-[12px]" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>Scans today</p>
          </motion.div>

          {/* This Week - Purple */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #8b5cf6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '12px' }}
            >
              <TrendingUp size={24} style={{ color: '#8b5cf6' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>This Week</p>
            <p className="text-[32px] font-bold" style={{ color: '#8b5cf6' }}>
              {stats.scansThisWeek.toLocaleString()}
            </p>
            <p className="text-[12px]" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>Last 7 days</p>
          </motion.div>

          {/* This Month - Orange */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-5"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #f97316',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-12 h-12 flex items-center justify-center mb-4"
              style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', borderRadius: '12px' }}
            >
              <BarChart3 size={24} style={{ color: '#f97316' }} />
            </div>
            <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>This Month</p>
            <p className="text-[32px] font-bold" style={{ color: '#f97316' }}>
              {stats.scansThisMonth.toLocaleString()}
            </p>
            <p className="text-[12px]" style={{ color: 'rgba(30, 27, 75, 0.4)' }}>Last 30 days</p>
          </motion.div>
        </div>

        {/* V5 QR Code Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white p-6 mb-8"
          style={{ 
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #14b8a6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          <div className="flex items-start gap-6">
            {location?.qr_code_url ? (
              <div 
                className="w-48 h-48 flex-shrink-0 p-4 flex items-center justify-center"
                style={{ 
                  backgroundColor: 'rgba(20, 184, 166, 0.05)',
                  border: '2px solid rgba(20, 184, 166, 0.2)',
                  borderRadius: '12px'
                }}
              >
                <img 
                  src={resolveAssetUrl(location.qr_code_url)}
                  alt="Location QR Code"
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div 
                className="w-48 h-48 flex items-center justify-center"
                style={{ 
                  backgroundColor: 'rgba(30, 27, 75, 0.02)',
                  borderRadius: '12px'
                }}
              >
                <QrCode size={64} style={{ color: 'rgba(30, 27, 75, 0.2)' }} />
              </div>
            )}
            <div className="flex-1">
              <h2 
                className="text-[24px] mb-4"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
              >
                Your Location QR Code
              </h2>
              <p className="text-[14px] mb-4" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                This unique QR code is printed on your coasters. When customers scan it, 
                they can discover all the businesses advertising at your location.
              </p>
              <div 
                className="p-4"
                style={{ 
                  backgroundColor: 'rgba(20, 184, 166, 0.05)',
                  border: '1px solid rgba(20, 184, 166, 0.1)',
                  borderRadius: '12px'
                }}
              >
                <h3 className="font-semibold text-[14px] mb-3" style={{ color: '#14b8a6' }}>How it works:</h3>
                <ul className="space-y-2">
                  {[
                    'Customer scans QR code on coaster',
                    'They see all active deals at your venue',
                    'Scan count increases in your analytics',
                    'Businesses reach new customers through your location'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                      <CheckCircle size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* V5 Coming Soon Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white p-8 text-center"
          style={{ 
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #8b5cf6',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          <div 
            className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '12px' }}
          >
            <BarChart3 size={32} style={{ color: '#8b5cf6' }} />
          </div>
          <h3 
            className="text-[24px] mb-4"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            More Analytics Coming Soon!
          </h3>
          <p className="text-[14px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
            We&apos;re working on detailed charts, graphs, and insights including:
          </p>
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {[
              { icon: '📊', title: 'Scan Trends', desc: 'Daily, weekly, and monthly scan patterns', color: '#14b8a6' },
              { icon: '🎯', title: 'Peak Hours', desc: 'When customers scan most often', color: '#f97316' },
              { icon: '📈', title: 'Growth Metrics', desc: 'Compare month-over-month performance', color: '#22c55e' },
              { icon: '🏆', title: 'Top Advertisers', desc: 'Most viewed businesses at your location', color: '#8b5cf6' }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="p-4 text-left"
                style={{ 
                  backgroundColor: 'rgba(30, 27, 75, 0.02)',
                  borderRadius: '12px',
                  borderLeft: `3px solid ${item.color}`
                }}
              >
                <h4 className="font-semibold text-[14px] mb-1" style={{ color: '#1e1b4b' }}>
                  {item.icon} {item.title}
                </h4>
                <p className="text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VenueAnalytics;
